'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { HugeiconsIcon } from '@hugeicons/react'
import { Bookmark02Icon as Bookmark02SolidIcon } from '@hugeicons-pro/core-solid-rounded'
import {
  ThumbsUpIcon,
  ThumbsDownIcon,
  Copy01Icon,
  Bookmark02Icon,
  Cancel01Icon,
  RotateLeft01Icon,
  ArrowDown01Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { ChatMarkdown } from '@/components/ChatMarkdown'
import { SupplyConfigWritePill } from '@/components/chat/SupplyConfigWritePill'
import { ThinkingToolCard, ToolCallCard, type ToolCallState } from '@/components/chat/ToolCallCard'
import type { MessageSegment } from '@/components/chat/messageSegment'
import { assistantMessageTimeDetailed, assistantMessageTimeShort } from '@/components/chat/assistantMessageTime'

const UI_ONLY_TOOLS = ['show_options', 'navigate', 'remember_user_preference', 'reinforce_memory']

/**
 * Serialize an assistant message + its tool calls into agent-pasteable
 * markdown. Used by the "Copy with tool calls" action so users can hand the
 * whole exchange — text, tool inputs, tool results, errors — to another
 * agent for debugging.
 */
function serializeMessageWithToolCalls(
  content: string,
  segments: MessageSegment[] | undefined,
  toolCalls: Map<string, ToolCallState>,
): string {
  const parts: string[] = []
  if (content.trim()) parts.push(content.trim())

  if (!segments || segments.length === 0) return parts.join('\n\n')

  const seenKeys = new Set<string>()
  for (const seg of segments) {
    if (seg.type !== 'tool') continue
    if (seenKeys.has(seg.toolKey)) continue
    seenKeys.add(seg.toolKey)

    const tc = toolCalls.get(seg.toolKey)
    if (!tc) continue
    if (UI_ONLY_TOOLS.includes(tc.name)) continue

    const block: string[] = []
    block.push(`### [tool: ${tc.name}] (${tc.status})`)
    try {
      block.push('input:')
      block.push('```json')
      block.push(JSON.stringify(tc.input ?? {}, null, 2))
      block.push('```')
    } catch {
      block.push('input: <unserializable>')
    }
    if (tc.result !== undefined) {
      try {
        block.push('result:')
        block.push('```json')
        block.push(JSON.stringify(tc.result, null, 2))
        block.push('```')
      } catch {
        block.push('result: <unserializable>')
      }
    }
    if (tc.error) {
      block.push(`error: ${tc.error}`)
    }
    parts.push(block.join('\n'))
  }

  return parts.join('\n\n')
}

function getQueuedId(toolCall: ToolCallState): string | null {
  if (!toolCall.result || typeof toolCall.result !== 'object') return null
  const queuedId = (toolCall.result as Record<string, unknown>).queued_id
  return typeof queuedId === 'string' ? queuedId : null
}

export interface ChatAssistantMessageProps {
  messageIndex: number
  messageId?: string
  content: string
  isTyping?: boolean
  /**
   * When true alongside isTyping, render a "still working..." label next
   * to the typing dots so the user knows the model is taking longer than
   * usual but hasn't broken. Driven by a 15s silence timer in
   * ChatInterface; harmless when undefined or false. (Phase 5 robustness.)
   */
  streamSlow?: boolean
  segments?: MessageSegment[]
  toolCalls: Map<string, ToolCallState>
  created_at?: string
  rootRef?: React.RefCallback<HTMLDivElement | null>
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  showActions: boolean
  bookmarkedMessageIds: Set<string>
  onToggleBookmark: (messageId: string) => void
  onCopy: () => void
  /**
   * Optional: copy this message's text along with all of its tool call
   * input/result blocks, formatted as agent-pasteable markdown. When
   * undefined, the "Copy with tool calls" menu item is hidden.
   */
  onCopyWithToolCalls?: (transcript: string) => void
  onThumbsUp: () => void
  onThumbsDown: () => void
  onLinkQuote: (url: string, text?: string) => void
  feedbackOpen: boolean
  feedbackRating: 'positive' | 'negative' | null
  feedbackText: string
  onFeedbackTextChange: (text: string) => void
  onFeedbackClose: () => void
  onFeedbackSubmit: () => void | Promise<void>
  submittingFeedback: boolean
  isRetryable?: boolean
  onRetry?: () => void
  /** When true, bookmark is visible but not clickable (e.g. assistant message not yet persisted). */
  bookmarkDisabled?: boolean
  /**
   * When true, the inner content is capped to a centered narrow column while
   * the outer (group/hover) wrapper stays full width. Used when the chat
   * panel is in viewport-fullscreen mode so messages don't span the entire
   * monitor.
   */
  narrow?: boolean
}

export function ChatAssistantMessage({
  messageIndex,
  messageId,
  content,
  isTyping,
  streamSlow,
  segments,
  toolCalls,
  created_at,
  rootRef,
  onMouseEnter,
  onMouseLeave,
  showActions,
  bookmarkedMessageIds,
  onToggleBookmark,
  onCopy,
  onCopyWithToolCalls,
  onThumbsUp,
  onThumbsDown,
  onLinkQuote,
  feedbackOpen,
  feedbackRating,
  feedbackText,
  onFeedbackTextChange,
  onFeedbackClose,
  onFeedbackSubmit,
  submittingFeedback,
  isRetryable = false,
  onRetry,
  bookmarkDisabled = false,
  narrow = false,
}: ChatAssistantMessageProps) {
  // When `narrow` is true, an inner wrapper caps the content to a centered
  // narrow column. The outer `group` div stays full width so hover/highlight
  // rows continue to span the full viewport (per Stephen). All siblings
  // (text content, action bar, feedback box) live inside the narrow wrapper
  // so they stay column-aligned.
  const narrowColCls = narrow ? 'mx-auto w-full max-w-3xl xl:max-w-4xl flex flex-col' : 'contents'
  // Scope the pill scan to THIS message's tool calls via segments.
  // The `toolCalls` Map is shared across the entire conversation; iterating
  // its values would render pills from every prior message's writes on every
  // assistant message (root cause of the 2026-06-19 ghost-pill bug). Each
  // message's tool calls are uniquely referenced by its tool-typed segments.
  const supplyQueueIds = Array.from(
    new Set(
      (segments ?? [])
        .filter((seg): seg is { type: 'tool'; toolKey: string } => seg.type === 'tool')
        .map(seg => toolCalls.get(seg.toolKey))
        .filter((tc): tc is ToolCallState => Boolean(tc))
        .map(getQueuedId)
        .filter((queueId): queueId is string => Boolean(queueId)),
    ),
  )
  const supplyWritePills = supplyQueueIds.length > 0 ? (
    <div className="mt-2 flex flex-wrap gap-2">
      {supplyQueueIds.map(queueId => (
        <SupplyConfigWritePill key={queueId} queueId={queueId} placement="message" />
      ))}
    </div>
  ) : null
  const firstVisibleToolSegmentIndex = segments?.findIndex(seg => {
    if (seg.type !== 'tool') return false
    const toolCall = toolCalls.get(seg.toolKey)
    return Boolean(toolCall && !UI_ONLY_TOOLS.includes(toolCall.name))
  }) ?? -1

  return (
    <div
      data-assistant-message
      data-msg-index={messageIndex}
      ref={rootRef}
      className="group flex w-full min-w-0 flex-col items-stretch"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={narrowColCls}>
      <div className="w-full min-w-0 px-2 py-2 text-[13px] leading-relaxed text-atrium-black">
        {isTyping && !content && (!segments || segments.length === 0) ? (
          <span className="flex items-center gap-2 py-1">
            <span className="flex gap-1">
              <span className="w-1.5 h-1.5 bg-atrium-muted rounded-full animate-bounce [animation-delay:-0.3s]" />
              <span className="w-1.5 h-1.5 bg-atrium-muted rounded-full animate-bounce [animation-delay:-0.15s]" />
              <span className="w-1.5 h-1.5 bg-atrium-muted rounded-full animate-bounce" />
            </span>
            {streamSlow ? (
              <span className="text-[11px] text-atrium-muted italic animate-pulse">
                still working…
              </span>
            ) : null}
          </span>
        ) : segments ? (
          <>
            {segments.map((seg, si) => {
              if (seg.type === 'thinking') {
                return <ThinkingToolCard key={si} content={seg.content} isStreaming={seg.isStreaming} />
              }
              if (seg.type === 'text') {
                const cleanText = seg.content
                  .replace(/<options>[\s\S]*?<\/options>/g, '')
                  .replace(/<attach_creative>[\s\S]*?<\/attach_creative>/g, '')
                  .replace(/<tool_call>[\s\S]*?<\/tool_call>/g, '')
                  .trim()
                return cleanText ? (
                  <ChatMarkdown key={si} content={cleanText} onLinkQuote={onLinkQuote} />
                ) : null
              }
              const toolCall = toolCalls.get(seg.toolKey)
              if (!toolCall || UI_ONLY_TOOLS.includes(toolCall.name)) return null
              if (si === firstVisibleToolSegmentIndex) {
                return (
                  <div key={si}>
                    {supplyWritePills}
                    <ToolCallCard toolCall={toolCall} />
                  </div>
                )
              }
              return <ToolCallCard key={si} toolCall={toolCall} />
            })}
          </>
        ) : (
          <>
            <ChatMarkdown content={content} onLinkQuote={onLinkQuote} />
            {supplyWritePills}
          </>
        )}
      </div>

      {!isTyping && (content || created_at) && (
        <div
          className={cn(
            'flex items-center gap-1 mt-0.5 ml-1 overflow-hidden transition-[max-width,opacity] duration-200 ease-out shrink-0',
            showActions ? 'max-w-[200px] opacity-100' : 'max-w-0 opacity-0 pointer-events-none',
          )}
        >
          {content ? (
            <>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span className={cn('inline-flex rounded', bookmarkDisabled && 'cursor-not-allowed')}>
                    <button
                      type="button"
                      disabled={bookmarkDisabled}
                      onClick={() => {
                        if (!bookmarkDisabled && messageId) onToggleBookmark(messageId)
                      }}
                      className={cn(
                        'rounded p-1 text-[#939294] transition-colors',
                        bookmarkDisabled
                          ? 'opacity-40 cursor-not-allowed'
                          : 'hover:text-atrium-black',
                      )}
                    >
                      <HugeiconsIcon
                        icon={
                          messageId && !bookmarkDisabled && bookmarkedMessageIds.has(messageId)
                            ? Bookmark02SolidIcon
                            : Bookmark02Icon
                        }
                        className={cn(
                          'size-4',
                          messageId && !bookmarkDisabled && bookmarkedMessageIds.has(messageId) ? 'text-accent-600' : '',
                        )}
                        strokeWidth={2}
                      />
                    </button>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {bookmarkDisabled
                    ? 'Bookmarks are available after the message is saved'
                    : messageId && bookmarkedMessageIds.has(messageId)
                      ? 'Remove bookmark'
                      : 'Bookmark'}
                </TooltipContent>
              </Tooltip>
              <div className="inline-flex items-center">
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={onCopy}
                      className={cn(
                        'p-1 text-[#939294] transition-colors hover:text-atrium-black',
                        onCopyWithToolCalls ? 'rounded-l' : 'rounded',
                      )}
                    >
                      <HugeiconsIcon icon={Copy01Icon} className="size-4" strokeWidth={2} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    Copy message
                  </TooltipContent>
                </Tooltip>
                {onCopyWithToolCalls ? (
                  <DropdownMenu>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            aria-label="Copy options"
                            className="rounded-r p-1 text-[#939294] transition-colors hover:text-atrium-black"
                          >
                            <HugeiconsIcon icon={ArrowDown01Icon} className="size-3" strokeWidth={2} />
                          </button>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs">
                        Copy options
                      </TooltipContent>
                    </Tooltip>
                    <DropdownMenuContent align="end" className="text-xs">
                      <DropdownMenuItem onClick={onCopy} className="text-xs">
                        Copy message text
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          const transcript = serializeMessageWithToolCalls(content, segments, toolCalls)
                          onCopyWithToolCalls(transcript)
                        }}
                        className="text-xs"
                      >
                        Copy with tool calls
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </div>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button type="button" onClick={onThumbsUp} className="rounded p-1 text-[#939294] transition-colors hover:text-atrium-black">
                    <HugeiconsIcon icon={ThumbsUpIcon} className="size-4" strokeWidth={2} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Good response
                </TooltipContent>
              </Tooltip>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button type="button" onClick={onThumbsDown} className="rounded p-1 text-[#939294] transition-colors hover:text-atrium-black">
                    <HugeiconsIcon icon={ThumbsDownIcon} className="size-4" strokeWidth={2} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Bad response
                </TooltipContent>
              </Tooltip>
              {isRetryable && onRetry ? (
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button type="button" onClick={onRetry} className="rounded p-1 text-[#939294] transition-colors hover:text-atrium-black">
                      <HugeiconsIcon icon={RotateLeft01Icon} className="size-4" strokeWidth={2} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    Retry
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </>
          ) : null}
          {created_at ? (
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span className="text-[10px] text-atrium-black/60 cursor-default tabular-nums shrink-0">
                  {assistantMessageTimeShort(created_at)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs max-w-xs text-left">
                {assistantMessageTimeDetailed(created_at)}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      )}

      {feedbackOpen && feedbackRating && (
        <div className="mt-2 p-3 bg-atrium-sidebar border border-atrium-border rounded-xl max-w-[85%] space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-atrium-black">
              {feedbackRating === 'positive' ? '👍 What was good?' : '👎 What could be better?'}
            </span>
            <button type="button" onClick={onFeedbackClose} className="text-atrium-muted hover:text-atrium-black">
              <HugeiconsIcon icon={Cancel01Icon} className="size-3.5" />
            </button>
          </div>
          <textarea
            value={feedbackText}
            onChange={e => onFeedbackTextChange(e.target.value)}
            placeholder="Optional feedback..."
            className="w-full text-xs border border-atrium-border rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-accent-600"
            rows={2}
          />
          <Button
            size="sm"
            disabled={submittingFeedback}
            className="text-xs h-7 bg-accent-600 hover:bg-accent-700 text-white"
            onClick={() => void onFeedbackSubmit()}
          >
            {submittingFeedback ? 'Sending…' : 'Submit'}
          </Button>
        </div>
      )}
      </div>
    </div>
  )
}
